import React from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { AuthCallback, ProtectedRoute, AdminRoute, VenueRoute } from './components/AuthHelpers';

// Pages
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import Onboarding from './pages/Onboarding';
import Discover from './pages/Discover';
import ActivityDetail from './pages/ActivityDetail';
import Search from './pages/Search';
import Saved from './pages/Saved';
import Profile from './pages/Profile';
import MapView from './pages/MapView';
import AdminDashboard from './pages/AdminDashboard';
import VenueDashboard from './pages/VenueDashboard';
import { CollectionsList, CollectionDetail } from './pages/Collections';
import Journal from './pages/Journal';
import Notifications from './pages/Notifications';

import './App.css';

// AppRouter handles the session_id check synchronously
const AppRouter = () => {
  const location = useLocation();

  // Check URL fragment for session_id (from Google OAuth)
  // This must happen synchronously during render to prevent race conditions
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      
      {/* Semi-public routes (work with/without auth) */}
      <Route path="/discover" element={<Discover />} />
      <Route path="/activity/:id" element={<ActivityDetail />} />
      <Route path="/search" element={<Search />} />
      <Route path="/map" element={<MapView />} />
      <Route path="/collections" element={<CollectionsList />} />
      <Route path="/collections/:id" element={<CollectionDetail />} />
      <Route path="/journal" element={<Journal />} />
      <Route path="/notifications" element={<Notifications />} />
      
      {/* Protected routes */}
      <Route path="/onboarding" element={
        <ProtectedRoute>
          <Onboarding />
        </ProtectedRoute>
      } />
      <Route path="/saved" element={
        <ProtectedRoute>
          <Saved />
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <Profile />
        </ProtectedRoute>
      } />
      
      {/* Venue routes */}
      <Route path="/venue/dashboard" element={
        <VenueRoute>
          <VenueDashboard />
        </VenueRoute>
      } />
      
      {/* Admin routes */}
      <Route path="/admin" element={
        <AdminRoute>
          <AdminDashboard />
        </AdminRoute>
      } />
      
      {/* Catch-all redirect */}
      <Route path="*" element={<Landing />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-center" richColors />
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
